// First-party, cookieless analytics tracker (Milestone 3).
//
// Records how anonymous visitors move through the landing page — page views,
// sections reached, CTA clicks, the form funnel, and time on page — and POSTs
// each event to POST /events (EventsController). There are no cookies and no
// persistent identifiers: unique visitors are counted server-side by a daily
// salted hash of IP + user-agent (see app/services/visitor_hash.rb).
//
// Attribution (referrer + UTM) is captured on the first view of a tab session
// and held in `sessionStorage` (session-scoped — not a persistent identifier),
// so it can be attached to every event and reused by the application form.
//
// Browser-only. Installed from the CLIENT Inertia entrypoint (inertia.ts) only,
// exactly like installStandaloneRecovery — never imported by ssr.tsx. Every
// entry point guards for `window`, so importing this module during SSR is inert.

const ENDPOINT = "/events"
const SECTION_THRESHOLD = 0.4 // fraction of a section visible to count "reached"

const ATTRIBUTION_KEY = "mt_analytics_attribution"
const PAGEVIEW_KEY = "mt_analytics_pageview_sent"

// Kept in lockstep with Event::EVENT_TYPES on the server.
type EventType =
  | "page_view"
  | "section_reached"
  | "click"
  | "form_start"
  | "field_blur"
  | "form_submit"
  | "time_on_page"

export type Attribution = {
  referrer: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
}

const EMPTY_ATTRIBUTION: Attribution = {
  referrer: null,
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

function clip(value: string | null, max: number): string | null {
  if (!value) return null
  const trimmed = value.trim()
  return trimmed ? trimmed.slice(0, max) : null
}

// Read referrer + UTM from the current URL. Used only to seed the session store
// on first view.
function readFromUrl(): Attribution {
  const params = new URLSearchParams(window.location.search)
  const utm = (key: string) => clip(params.get(key), 255)
  return {
    referrer: clip(document.referrer, 2000),
    utm_source: utm("utm_source"),
    utm_medium: utm("utm_medium"),
    utm_campaign: utm("utm_campaign"),
    utm_content: utm("utm_content"),
    utm_term: utm("utm_term"),
  }
}

// Attribution for this tab session: captured from the URL on the first view and
// persisted in sessionStorage so later events (and the form) share one source of
// truth. SSR-safe: returns empties on the server. Safe to call before the
// tracker is installed (e.g. from the form) — it seeds the store on first use.
export function getAttribution(): Attribution {
  if (!isBrowser()) return EMPTY_ATTRIBUTION
  try {
    const stored = window.sessionStorage.getItem(ATTRIBUTION_KEY)
    if (stored) return JSON.parse(stored) as Attribution
    const fresh = readFromUrl()
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(fresh))
    return fresh
  } catch {
    // sessionStorage can throw (private mode / disabled). Fall back to the URL.
    return readFromUrl()
  }
}

function deviceType(width: number): "mobile" | "tablet" | "desktop" {
  if (width < 768) return "mobile"
  if (width < 1024) return "tablet"
  return "desktop"
}

// --- send one event -------------------------------------------------------
// Each event is its own request (fetch with keepalive; sendBeacon for the leave
// event so it survives unload). Analytics is best-effort: failures are swallowed.
function sendEvent(
  eventType: EventType,
  opts: { label?: string | null; value?: number | null; beacon?: boolean } = {},
): void {
  // Only emit while on a trackable page (see syncAnalytics). This keeps events
  // off auth-area pages and off any page viewed while signed in, even though the
  // delegated listeners are installed once and live for the whole SPA session.
  if (!isBrowser() || !trackable) return
  const attribution = getAttribution()
  const payload = {
    event_type: eventType,
    path: window.location.pathname,
    label: opts.label ?? null,
    value: opts.value ?? null,
    device_type: deviceType(window.innerWidth),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    ...attribution,
  }
  const body = JSON.stringify(payload)

  if (opts.beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }))
    return
  }

  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {
    /* best-effort */
  })
}

// --- module state (client only) -------------------------------------------
// Analytics is scoped to the public marketing surface. Only these Inertia page
// components are tracked; every other page (login, dashboard, settings, …) and
// any page viewed while signed in is left alone. Add future public marketing
// pages here.
const TRACKED_COMPONENTS = new Set(["Home"])

let listenersInstalled = false // document listeners are wired once, then reused
let trackable = false // is the CURRENT page a tracked page (and visitor anon)?
let sectionObserver: IntersectionObserver | null = null
let formStarted = false
const blurredFields = new Set<string>()
let timeSent = false
let pageEnteredAt = 0

function isTrackablePage(component: string | undefined, currentUser: unknown): boolean {
  if (currentUser) return false // never track signed-in visits (Matt's own)
  return TRACKED_COMPONENTS.has(component ?? "")
}

// page_view fires once per tab session (a sessionStorage flag dedupes re-mounts
// and reloads within the same session).
function firePageViewOnce(): void {
  let already = false
  try {
    already = window.sessionStorage.getItem(PAGEVIEW_KEY) === "1"
  } catch {
    already = false
  }
  if (already) return
  try {
    window.sessionStorage.setItem(PAGEVIEW_KEY, "1")
  } catch {
    /* ignore */
  }
  sendEvent("page_view")
}

// --- section reached (hero through form) ----------------------------------
// enterTrackablePage runs right after the page mounts. With client-only
// rendering (dev, or any non-SSR page) the section elements are not in the DOM
// yet at that moment, so we wait for them via requestAnimationFrame before
// observing. With SSR they exist on the first frame. Bounded so a page with no
// sections gives up quickly.
function observeSectionsWhenReady(attempts = 0): void {
  if (typeof IntersectionObserver === "undefined") return
  if (document.querySelectorAll("[data-section]").length === 0) {
    if (attempts < 120 && typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => observeSectionsWhenReady(attempts + 1))
    }
    return
  }
  observeSections()
}

function observeSections(): void {
  const sections = document.querySelectorAll<HTMLElement>("[data-section]")
  if (sections.length === 0) return

  sectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const el = entry.target as HTMLElement
        sendEvent("section_reached", { label: el.dataset.section || null })
        sectionObserver?.unobserve(el) // fire once per section
      }
    },
    { threshold: SECTION_THRESHOLD },
  )
  sections.forEach((section) => sectionObserver?.observe(section))
}

// --- CTA / link clicks -----------------------------------------------------
function onClick(e: MouseEvent): void {
  const target = e.target
  if (!(target instanceof Element)) return
  const el = target.closest<HTMLElement>("[data-track-click]")
  if (!el) return
  sendEvent("click", { label: el.dataset.trackClick || null })
}

// --- application form funnel ----------------------------------------------
const FORM_SCOPE = "#apply"
const FIELD_SELECTOR = "input, select, textarea"

// A tracked form field: a named control inside #apply, excluding the honeypot.
function trackedField(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null
  if (!target.matches(FIELD_SELECTOR)) return null
  if (!target.closest(FORM_SCOPE)) return null
  const name = target.getAttribute("name")
  if (!name || name === "company_website") return null
  return target
}

function onFocusIn(e: FocusEvent): void {
  if (!trackedField(e.target)) return
  if (formStarted) return
  formStarted = true
  sendEvent("form_start")
}

function onFocusOut(e: FocusEvent): void {
  const field = trackedField(e.target)
  if (!field) return
  const name = field.getAttribute("name") as string
  if (blurredFields.has(name)) return // one blur per field
  blurredFields.add(name)
  sendEvent("field_blur", { label: name })
}

function onSubmit(e: Event): void {
  const target = e.target
  if (!(target instanceof HTMLElement)) return
  if (!target.closest(FORM_SCOPE)) return
  sendEvent("form_submit")
}

// --- time on page ----------------------------------------------------------
// Fire once, on the first "leaving" signal (tab hidden or page unloading), via
// sendBeacon so it survives unload. value = whole seconds on the page.
function sendTimeOnPage(): void {
  if (timeSent) return
  timeSent = true
  const seconds = Math.max(0, Math.round((performance.now() - pageEnteredAt) / 1000))
  sendEvent("time_on_page", { value: seconds, beacon: true })
}

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") sendTimeOnPage()
}

// Document/window listeners are delegated, so they're wired once and reused for
// the life of the tab; every send is gated on `trackable`.
function ensureListeners(): void {
  if (listenersInstalled) return
  listenersInstalled = true
  document.addEventListener("click", onClick, true)
  document.addEventListener("focusin", onFocusIn)
  document.addEventListener("focusout", onFocusOut)
  document.addEventListener("submit", onSubmit, true)
  document.addEventListener("visibilitychange", onVisibilityChange)
  window.addEventListener("pagehide", sendTimeOnPage)
}

function enterTrackablePage(): void {
  getAttribution() // seed the session store from the URL on first view
  pageEnteredAt = performance.now()
  timeSent = false
  formStarted = false
  blurredFields.clear()
  ensureListeners()
  firePageViewOnce()
  observeSectionsWhenReady()
}

function leaveTrackablePage(): void {
  sendTimeOnPage() // record time on the page we're leaving (runs while trackable)
  sectionObserver?.disconnect()
  sectionObserver = null
}

// Reconcile the tracker with the current page. Called from inertia.ts on the
// initial load and on every Inertia navigation, so tracking follows the visitor
// across SPA transitions: it turns on when they land on a tracked marketing page
// (and are not signed in) and off the moment they navigate to a non-tracked page
// (login, dashboard, …) or are signed in. No-op on the server. Idempotent when
// the trackable state is unchanged.
export function syncAnalytics({
  component,
  currentUser,
}: {
  component?: string
  currentUser?: unknown
}): void {
  if (!isBrowser()) return
  const nowTrackable = isTrackablePage(component, currentUser)
  if (nowTrackable === trackable) return

  if (nowTrackable) {
    trackable = true
    enterTrackablePage()
  } else {
    // Record the final time-on-page while still trackable, then switch off.
    leaveTrackablePage()
    trackable = false
  }
}
